import { useCallback, useMemo, useState } from 'react'

import { useFormData } from 'brief-form'
import { FilterCompanyCustomersWebPage } from 'commons/service/bank/companyDetails'
import { useTableState } from 'ui/components/Table'

export const useComponentsEssence = (
  companyId: number,
  onFilterCompanyCustomersWebPage: (filter: FilterCompanyCustomersWebPage) => void
) => {
  const [loading, setLoading] = useState(false)

  const defaultFilter = useMemo(
    () => ({
      relationshipId: companyId,
      page: 1,
      size: 10,
      customersName: undefined
    }),
    [companyId]
  )

  const [tableFilter, setTableFilter] = useState<FilterCompanyCustomersWebPage>(defaultFilter)

  const tableState = useTableState({ filter: {} })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      setLoading(true)

      tableState.onPageChange(page, size)

      const filter = {
        ...tableFilter,
        page,
        size
      }

      setTableFilter(filter)

      await onFilterCompanyCustomersWebPage(filter)

      setLoading(false)
    },
    [onFilterCompanyCustomersWebPage, tableFilter, tableState]
  )

  const onFilterChange = async (value: FilterCompanyCustomersWebPage) => {
    tableState.filterForm.set({ value })

    setLoading(true)

    const filter = {
      ...value,
      page: defaultFilter.page,
      size: defaultFilter.size
    }

    setTableFilter(filter)

    await onFilterCompanyCustomersWebPage(filter)

    setLoading(false)
  }

  const filterForm = useFormData<FilterCompanyCustomersWebPage>({
    initialValue: {
      relationshipId: companyId,
      page: defaultFilter.page,
      size: defaultFilter.size,
      customersName: undefined
    }
  })

  const originalChangeHandler = filterForm.config.onChange
  filterForm.config.onChange = useCallback(
    (v, e) => {
      onFilterChange(v)
      originalChangeHandler(v, e)
    },
    [originalChangeHandler, tableState]
  )

  const clearFilter = useCallback(() => {
    onFilterChange(defaultFilter)
    filterForm.set({ reset: true })
  }, [onFilterChange, filterForm])

  return {
    loading,
    tableState,
    filterForm,
    changePagination,
    clearFilter
  }
}
